import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Landing from './pages/Landing';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Home from './pages/Home';
import GameHub from './pages/GameHub';
import QuickGame from './pages/QuickGame';
import SimpleWallet from './pages/SimpleWallet';
import SandboxPay from './pages/SandboxPay';
import Profile from './pages/Profile';
import Circle from './pages/Circle';
import GolferProfile from './pages/GolferProfile';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import AdminWithdrawals from './pages/AdminWithdrawals';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminDisputes from './pages/admin/AdminDisputes';
import AdminAudit from './pages/admin/AdminAudit';
import JoinGame from './pages/JoinGame';
import NewChat from './pages/NewChat';
import Chat from './pages/Chat';
import './index.css';
import './styles/mobile.css';
import { isLocalVisualReview } from './utils/localReview';
import LocalReview from './pages/LocalReview';
import SeoManager from './components/SeoManager';
import HowItWorks from './pages/HowItWorks';
import GolfSideBets from './pages/GolfSideBets';
import NotFound from './pages/NotFound';
import AnalyticsManager from './components/AnalyticsManager';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user || isLocalVisualReview ? <>{children}</> : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? <Navigate to="/" /> : <>{children}</>;
};

// Root: the public landing page for visitors, the app home for signed-in users.
const RootRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    );
  }
  // A pending WhatsApp/share invite (stashed before signing in) takes priority
  // once the user is authenticated, so the join flow resumes automatically.
  if (user) {
    let pending: string | null = null;
    try { pending = localStorage.getItem('pendingJoinCode'); } catch { /* ignore */ }
    if (pending) return <Navigate to={`/join/${pending}`} replace />;
  }
  return user || isLocalVisualReview ? <Home /> : <Landing />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <SeoManager />
        <AnalyticsManager />
        <Routes>
            {isLocalVisualReview && <Route path="/review" element={<LocalReview />} />}
            {isLocalVisualReview && <Route path="/review/landing" element={<Landing />} />}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            {/* Recovery flows work whether or not you're signed in (token-based) */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* Shared WhatsApp/game invite link — works signed in or out */}
            <Route path="/join/:code" element={<JoinGame />} />
            {/* Legal pages — public, needed for Google OAuth + real-money use */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/golf-side-bets" element={<GolfSideBets />} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/game" element={<PrivateRoute><GameHub /></PrivateRoute>} />
            <Route path="/game/:id" element={<PrivateRoute><GameHub /></PrivateRoute>} />
            <Route path="/quick-game" element={<PrivateRoute><QuickGame /></PrivateRoute>} />
            <Route path="/wallet" element={<PrivateRoute><SimpleWallet /></PrivateRoute>} />
            {/* Faux payment gateway for sandbox testing (backend FAUX_PAYMENTS) */}
            <Route path="/pay/sandbox" element={<PrivateRoute><SandboxPay /></PrivateRoute>} />
            {/* PayFast redirects the user back here after payment. */}
            <Route path="/deposit/return" element={<PrivateRoute><SimpleWallet /></PrivateRoute>} />
            <Route path="/deposit/cancel" element={<PrivateRoute><SimpleWallet /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            {/* Social: find golfers, view profiles, message your circle */}
            <Route path="/golfers" element={<PrivateRoute><Circle /></PrivateRoute>} />
            <Route path="/golfers/:id" element={<PrivateRoute><GolferProfile /></PrivateRoute>} />
            <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
            <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
            {/* Admin — AdminLayout redirects non-admins; backend enforces access */}
            <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
            <Route path="/admin/users" element={<PrivateRoute><AdminUsers /></PrivateRoute>} />
            <Route path="/admin/users/:id" element={<PrivateRoute><AdminUserDetail /></PrivateRoute>} />
            <Route path="/admin/disputes" element={<PrivateRoute><AdminDisputes /></PrivateRoute>} />
            <Route path="/admin/audit" element={<PrivateRoute><AdminAudit /></PrivateRoute>} />
            <Route path="/admin/withdrawals" element={<PrivateRoute><AdminWithdrawals /></PrivateRoute>} />
            <Route path="/messages/new/:userId" element={<PrivateRoute><NewChat /></PrivateRoute>} />
            <Route path="/messages/thread/:threadId" element={<PrivateRoute><Chat /></PrivateRoute>} />
            <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
